import { lastValueFrom } from '@onviobr/helpers';
import { profileManager } from '@onviobr/profile-services';
import { environment, featureResourcePermissions, sessionManager, } from '@onviobr/security-services';
import { getAppConfig } from '@onviobr/app-information';
import { getIdStitchValues } from '@onviobr/tr-id-stitch';
import { initialize as initializePendo, trackEvent, } from './tr-pendo';
function ensureApplication(session, applicationName) {
    if (!Object.keys(session.UdsSessionBindings.RoutingTable).includes(applicationName)) {
        const error = new Error(`The application name '${applicationName}' it's not an Onvio Vertical Name declared in the Routing Table`);
        error.code = 'NOT_VALID_APP';
        throw error;
    }
}
function optionalObjectMap(target, source) {
    if (!target || !source) {
        return;
    }
    for (const key in source) {
        if (key && source[key]) {
            target[key] = source[key];
        }
    }
}
const corePendoAPIKey = '3d74f51c-2613-4bc2-66d8-3f126c9a34d8';
/**
 * Injects and initialize the Pendo Agent with all the Bluemoon Session information
 * @public
 * @param pendoAPIKey - Optional Pendo API Key (Default value is the Bluemoon shared key)
 * @param additionalAccountMetadata - Optional additional data of the account that will be sent to Pendo
 * @param additionalVisitorMetadata - Optional additional data of the visitor that will be sent to Pendo
 */
export async function initialize(pendoAPIKey, additionalAccountMetadata, additionalVisitorMetadata) {
    var _a, _b, _c, _d, _e, _f;
    try {
        const { name: appName } = getAppConfig();
        pendoAPIKey = pendoAPIKey !== null && pendoAPIKey !== void 0 ? pendoAPIKey : corePendoAPIKey;
        const [session, profile, env] = await Promise.all([
            lastValueFrom(sessionManager.sessionAndBindings),
            lastValueFrom(profileManager.currentProfile),
            lastValueFrom(sessionManager.environment),
        ]);
        ensureApplication(session, appName);
        const frps = session.UdsSessionBindings.FeatureResourcePermissions.FeatureResourcePermissionsMap;
        const isStaff = featureResourcePermissions.hasEvery(frps, 'Platform.DefaultStaff');
        const isClient = featureResourcePermissions.hasEvery(frps, 'Platform.DefaultClient');
        const isClientEmployee = featureResourcePermissions.hasEvery(frps, 'BREmployeePortal.BREmployeePortal');
        const isAccountantUser = featureResourcePermissions.hasEvery(frps, 'Onbalance.AccountantView');
        const isBusinessUser = featureResourcePermissions.hasEvery(frps, ['Samba.BookkeepingApp', 'Samba.Bookkeeping']);
        const contactId = (_b = (_a = session === null || session === void 0 ? void 0 : session.UdsSessionBindings) === null || _a === void 0 ? void 0 : _a.ApplicationConfiguration) === null || _b === void 0 ? void 0 : _b.ContactId;
        const countryCode = env.countryCode ? env.countryCode.toUpperCase() : 'US';
        const companyId = (_d = (_c = session === null || session === void 0 ? void 0 : session.UdsSessionBindings) === null || _c === void 0 ? void 0 : _c.ApplicationConfiguration) === null || _d === void 0 ? void 0 : _d.CompanyId;
        const isTestAccount = ((_f = (_e = session === null || session === void 0 ? void 0 : session.UdsSessionBindings) === null || _e === void 0 ? void 0 : _e.ApplicationConfiguration) === null || _f === void 0 ? void 0 : _f.TestFirm) === 'true';
        const account = {
            id: companyId,
            isTestAccount: isTestAccount,
            countryCode: countryCode,
            environment: environment.environmentName,
        };
        if (countryCode === 'AR') {
            const accountApplications = {
                ARApplicationMessenger: 'Applications.BRChat',
                ARApplicationIncomeTax: 'Applications.ARIncomeTax',
                ARApplicationGrossIncomeTax: 'Applications.ARGrossIncomeTax',
                ARApplicationInvestmentAccounts: 'Applications.ARInvestmentAccounts',
                ARApplicationSingleTaxpayer: 'Applications.ARSingleTaxpayer',
            };
            const accountMemberships = {
                ARMembershipZafiro: 'OnvioAR.Zafiro',
                ARMembershipGold: 'OnvioAR.Gold',
                ARMembershipBlack: 'OnvioAR.Black',
                ARMembershipPlatinum: 'OnvioAR.Platinum',
                ARMembershipSilver: 'OnvioAR.Silver',
                ARMembershipSilverPlus: 'OnvioAR.SilverPlus',
                ARMembershipPlatform: 'OnvioAR.Platform',
                ARMembershipStarter: 'OnvioAR.Starter',
            };
            Object.keys(accountApplications).forEach(key => {
                account[key] = featureResourcePermissions.hasEvery(frps, accountApplications[key]);
            });
            Object.keys(accountMemberships).forEach(key => {
                account[key] = featureResourcePermissions.hasEvery(frps, accountMemberships[key]);
            });
        }
        const pendoData = {
            apiKey: pendoAPIKey,
            visitor: {
                id: contactId,
                application: appName,
            },
            account: account,
        };
        if (profile.language) {
            pendoData.visitor.profileLanguage = profile.language;
        }
        if (isAccountantUser) {
            pendoData.visitor.visitorType = 'Accountant User';
        }
        else if (isBusinessUser) {
            pendoData.visitor.visitorType = 'Business User';
        }
        else if (isClientEmployee) {
            pendoData.visitor.visitorType = 'Client Employee';
        }
        else if (isStaff) {
            pendoData.visitor.visitorType = 'Staff';
        }
        else if (isClient) {
            pendoData.visitor.visitorType = 'Client';
        }
        else {
            pendoData.visitor.visitorType = 'Undetermined';
        }
        if (session.UdsSessionBindings.ApplicationConfiguration.EmsFirmId) {
            pendoData.account.emsFirmID = session.UdsSessionBindings.ApplicationConfiguration.EmsFirmId;
        }
        optionalObjectMap(pendoData.account, additionalAccountMetadata);
        optionalObjectMap(pendoData.visitor, additionalVisitorMetadata);
        if (profile.email) {
            await setStitchIdFieldsInPendoData(profile.email, pendoData);
        }
        await initializePendo(pendoAPIKey, pendoData);
    }
    catch (error) {
        if (error.code === 'NOT_VALID_APP') {
            throw error;
        }
        console.warn('Error initializing Pendo', error);
    }
}
async function setStitchIdFieldsInPendoData(emailAddress, pendoData) {
    const stitchValues = await getIdStitchValues(emailAddress);
    if (stitchValues.adobeEcid && stitchValues.emailAddress) {
        pendoData.visitor.adobeEcid = stitchValues.adobeEcid;
        pendoData.visitor.emailHash = stitchValues.emailHash;
    }
}
/**
 * Tracks an alert into Pendo
 * @public
 * @param alertType - Type the alert
 * @param message - Message of the alert
 * @param timeout - Timeout of the alert
 */
export function trackAlert(alertType, message, timeout) {
    trackEvent(`Alert Message | ${alertType}`, {
        message,
        timed: timeout ? 'true' : 'false',
        timeout: timeout ? timeout : 0,
    });
}
